import React, { useState, useEffect, useCallback } from "react"
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material"
import { SharedFilterValues } from "./SharedFilters"
import ExportButton from "./ExportButton"
import reportsService from "../../services/reportsService"
import { formatCentsWithSymbol } from "../../utils/formatCurrency"
import { useTranslation } from "react-i18next"

interface RevenueByCategoryTabProps {
    filters: SharedFilterValues
    companyId: string
}

const fmtC = (v: number, c: string = "EUR") => formatCentsWithSymbol(v, c)

const RevenueByCategoryTab: React.FC<RevenueByCategoryTabProps> = ({
    filters,
    companyId,
}) => {
    const { t } = useTranslation()
    const [data, setData] = useState<any>(null)
    const [loading, setLoading] = useState(false)

    const loadData = useCallback(async () => {
        if (!companyId) return
        setLoading(true)
        try {
            const result = await reportsService.getRevenueByCategory(
                companyId,
                {
                    outletId: filters.outletId || undefined,
                    registerId: filters.registerId || undefined,
                    dateFrom: filters.dateFrom || undefined,
                    dateTo: filters.dateTo || undefined,
                }
            )
            setData(result)
        } catch (err) {
            console.error("Failed to load revenue by category:", err)
        } finally {
            setLoading(false)
        }
    }, [companyId, filters])

    useEffect(() => {
        loadData()
    }, [loadData])

    const revenue = data?.revenue || []
    const totals = data?.totals || {}

    return (
        <Box>
            {loading ? (
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <CircularProgress />
                </Box>
            ) : revenue.length > 0 ? (
                <>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            gap: 3,
                            alignItems: "center",
                            flexWrap: "wrap",
                        }}
                    >
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.revenueByCategoryTab.summary.totalHt"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.net_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.revenueByCategoryTab.summary.totalVat"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.vat_total || 0)}
                        </Typography>
                        <Typography variant="body2">
                            <strong>
                                {t(
                                    "exportsComponents.revenueByCategoryTab.summary.totalTtc"
                                )}
                                :
                            </strong>{" "}
                            {fmtC(totals.gross_total || 0)}
                        </Typography>
                        <Box sx={{ ml: "auto" }}>
                            <ExportButton
                                companyId={companyId}
                                reportType="revenue_by_category"
                                filters={filters}
                            />
                        </Box>
                    </Paper>
                    <TableContainer component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>
                                        {t(
                                            "exportsComponents.revenueByCategoryTab.columns.category"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.revenueByCategoryTab.columns.items"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t(
                                            "exportsComponents.revenueByCategoryTab.columns.qtySold"
                                        )}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ht")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.vat")}
                                    </TableCell>
                                    <TableCell align="right">
                                        {t("exportsComponents.common.ttc")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {revenue.map((row: any, i: number) => {
                                    const isExternal =
                                        row.category_id === "external"
                                    return (
                                        <TableRow key={i} hover>
                                            <TableCell>
                                                {row.category_name}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isExternal
                                                    ? "-"
                                                    : row.item_count}
                                            </TableCell>
                                            <TableCell align="right">
                                                {isExternal
                                                    ? "-"
                                                    : row.quantity_sold}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    row.net_revenue || 0,
                                                    row.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    row.vat_revenue || 0,
                                                    row.currency
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {fmtC(
                                                    row.gross_revenue || 0,
                                                    row.currency
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            ) : (
                <Box>
                    <Paper
                        sx={{
                            p: 2,
                            mb: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                        }}
                    >
                        <ExportButton
                            companyId={companyId}
                            reportType="revenue_by_category"
                            filters={filters}
                        />
                    </Paper>
                    <Paper sx={{ p: 3, textAlign: "center" }}>
                        <Typography variant="body2" color="text.secondary">
                            {t("exportsComponents.revenueByCategoryTab.empty")}
                        </Typography>
                    </Paper>
                </Box>
            )}
        </Box>
    )
}

export default RevenueByCategoryTab
